import * as culori from "culori";
import type { Hsl } from "culori";

export type ColorFormat = "hex" | "rgb" | "hsl" | "oklch";

export const formatNumber = (num?: number) => {
	if (!num) return "0";
	return num % 1 === 0 ? num : num.toFixed(4);
};

export const formatHsl = (hsl: Hsl) => {
	return `hsl(${formatNumber(hsl.h)} ${formatNumber(hsl.s * 100)}% ${formatNumber(hsl.l * 100)}%)`;
};

export const colorFormatter = (
	colorValue: string,
	format: ColorFormat = "hsl",
): string => {
	try {
		const color = culori.parse(colorValue);
		if (!color) throw new Error("Invalid color input");

		switch (format) {
			case "hsl": {
				const hsl = culori.converter("hsl")(color);

				return `${formatNumber(hsl.h)} ${formatNumber(hsl.s * 100)}% ${formatNumber(hsl.l * 100)}%`;
			}
			case "rgb":
				return culori.formatRgb(color); // e.g., "rgb(64, 128, 192)"
			case "oklch": {
				const oklch = culori.converter("oklch")(color);
				return `oklch(${formatNumber(oklch.l)} ${formatNumber(oklch.c)} ${formatNumber(oklch.h)})`;
			}
			case "hex":
				return culori.formatHex(color); // e.g., "#4080c0"
			default:
				return colorValue;
		}
	} catch (error) {
		console.error(`Failed to convert color: ${colorValue}`, error);
		return colorValue;
	}
};

export const getShadowMap = (styles: Record<string, string>) => {
	const shadowColor = styles["shadow-color"];
	const hsl = colorFormatter(shadowColor, "hsl");
	const offsetX = styles["shadow-offset-x"];
	const offsetY = styles["shadow-offset-y"];
	const blur = styles["shadow-blur"];
	const spread = styles["shadow-spread"];
	const opacity = parseFloat(styles["shadow-opacity"]);
	const color = (opacityMultiplier: number) =>
		`hsl(${hsl} / ${(opacity * opacityMultiplier).toFixed(2)})`;

	const secondLayer = (fixedOffsetY: string, fixedBlur: string): string => {
		// Use the same offsetX as the first layer
		const offsetX2 = offsetX;
		// Use the fixed offsetY specific to the shadow size
		const offsetY2 = fixedOffsetY;
		// Use the fixed blur specific to the shadow size
		const blur2 = fixedBlur;
		// Calculate spread relative to the first layer's spread variable
		const spread2 = `${(parseFloat(spread?.replace("px", "") ?? "0") - 1).toString()}px`;
		// Use the same color function (opacity can still be overridden by --shadow-opacity)
		const color2 = color(1.0); // Default opacity for second layer is 0.1 in examples

		return `${offsetX2} ${offsetY2} ${blur2} ${spread2} ${color2}`;
	};

	// Map shadow names to their CSS variable string structures
	const shadowMap: { [key: string]: string } = {
		// Single layer shadows - use base variables directly
		"shadow-2xs": `${offsetX} ${offsetY} ${blur} ${spread} ${color(0.5)}`, // Assumes vars set appropriately (e.g., y=1, blur=0, spread=0)
		"shadow-xs": `${offsetX} ${offsetY} ${blur} ${spread} ${color(0.5)}`, // Assumes vars set appropriately (e.g., y=1, blur=2, spread=0)
		"shadow-2xl": `${offsetX} ${offsetY} ${blur} ${spread} ${color(2.5)}`, // Assumes vars set appropriately (e.g., y=25, blur=50, spread=-12)

		// Two layer shadows - use base vars for layer 1, mix fixed/calculated for layer 2
		"shadow-sm": `${offsetX} ${offsetY} ${blur} ${spread} ${color(
			1.0,
		)}, ${secondLayer("1px", "2px")}`,
		shadow: `${offsetX} ${offsetY} ${blur} ${spread} ${color(1.0)}, ${secondLayer("1px", "2px")}`, // Alias for the 'shadow:' example line

		"shadow-md": `${offsetX} ${offsetY} ${blur} ${spread} ${color(
			1.0,
		)}, ${secondLayer("2px", "4px")}`,

		"shadow-lg": `${offsetX} ${offsetY} ${blur} ${spread} ${color(
			1.0,
		)}, ${secondLayer("4px", "6px")}`,

		"shadow-xl": `${offsetX} ${offsetY} ${blur} ${spread} ${color(
			1.0,
		)}, ${secondLayer("8px", "10px")}`,
	};

	return shadowMap;
};
